import { useEffect, useState } from "react";
import { Loader2, Save, FileText, Home, Pencil, Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { GlassCard } from "../../components/common/ui";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import RichTextEditor from "../../components/admin/RichTextEditor";
import SingleImageUpload from "../../components/admin/SingleImageUpload";
import SeoTab from "../../components/seo/SeoTab";
import api, { formatApiError } from "../../lib/api";

const inp = "mt-1 bg-black/30 border-white/15 text-white";

function HomepageTab() {
  const [cms, setCms] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/cms/homepage").then(({ data }) => setCms(data)).catch(() => {}); }, []);
  if (!cms) return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin text-gold" /> Loading…</div>;

  const setBlock = (block, key, val) => setCms((c) => ({ ...c, [block]: { ...(c[block] || {}), [key]: val } }));
  const setArr = (block, next) => setCms((c) => ({ ...c, [block]: next }));
  const addItem = (block, tmpl) => setArr(block, [...(cms[block] || []), tmpl]);
  const updItem = (block, idx, key, val) => setArr(block, (cms[block] || []).map((it, i) => (i === idx ? { ...it, [key]: val } : it)));
  const delItem = (block, idx) => setArr(block, (cms[block] || []).filter((_, i) => i !== idx));
  const moveItem = (block, idx, dir) => {
    const arr = [...(cms[block] || [])];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setArr(block, arr);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/cms/homepage", {
        announcement_bar: cms.announcement_bar, hero: cms.hero, countdown: cms.countdown,
        vip: cms.vip, seasonal_banner: cms.seasonal_banner, seasons: cms.seasons, occasions: cms.occasions,
      });
      toast.success("Homepage content saved");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const H = cms.hero || {}, A = cms.announcement_bar || {}, C = cms.countdown || {}, V = cms.vip || {}, SB = cms.seasonal_banner || {};
  return (
    <div className="space-y-6" data-testid="cms-homepage">
      <p className="text-sm text-white/50">Edit the approved homepage content. The design and layout are locked — only the copy, images and toggles below are editable.</p>

      <GlassCard className="p-6" hover={false}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-playfair text-lg text-white">Selling Seasons</h3>
          <Button size="sm" variant="outline" className="border-white/20 text-white gap-1" data-testid="cms-add-season" onClick={() => addItem("seasons", { id: Math.random().toString(36).slice(2, 10), enabled: true, name: "", sales_start: "", sales_end: "", image: "", eyebrow: "", heading: "", description: "", cta_label: "Browse the Collection", cta_url: "/fireworks" })}><Plus className="h-4 w-4" /> Add season</Button>
        </div>
        <p className="text-sm text-white/50 mb-4">Seasonal campaign banners with a live countdown. A season shows on the homepage while active and within its sales dates. Leave dates blank to show whenever active.</p>
        <div className="space-y-5">
          {(cms.seasons || []).map((s, idx) => (
            <div key={s.id || idx} className="rounded-xl border border-white/10 bg-black/20 p-4 space-y-3" data-testid={`cms-season-${idx}`}>
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-wide text-white/50 flex-1">{s.name || s.heading || `Season ${idx + 1}`}</span>
                <span className="text-[11px] text-white/50">{s.enabled !== false ? "Active" : "Inactive"}</span>
                <Switch data-testid={`cms-season-toggle-${idx}`} checked={s.enabled !== false} onCheckedChange={(v) => updItem("seasons", idx, "enabled", v)} />
                <button type="button" onClick={() => moveItem("seasons", idx, -1)} className="text-white/50 hover:text-white p-1"><ArrowUp className="h-4 w-4" /></button>
                <button type="button" onClick={() => moveItem("seasons", idx, 1)} className="text-white/50 hover:text-white p-1"><ArrowDown className="h-4 w-4" /></button>
                <button type="button" onClick={() => delItem("seasons", idx)} className="text-firered/80 hover:text-firered p-1" data-testid={`cms-del-season-${idx}`}><Trash2 className="h-4 w-4" /></button>
              </div>
              <div><Label className="text-white/70 text-xs">Season image</Label><div className="mt-1"><SingleImageUpload value={s.image || ""} onChange={(url) => updItem("seasons", idx, "image", url)} testid={`cms-season-image-${idx}`} /></div></div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label className="text-white/70 text-xs">Season name</Label><Input value={s.name || ""} onChange={(e) => updItem("seasons", idx, "name", e.target.value)} className={inp} /></div>
                <div><Label className="text-white/70 text-xs">Eyebrow</Label><Input value={s.eyebrow || ""} onChange={(e) => updItem("seasons", idx, "eyebrow", e.target.value)} className={inp} /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label className="text-white/70 text-xs">Sales start date</Label><Input type="date" data-testid={`cms-season-start-${idx}`} value={(s.sales_start || "").slice(0, 10)} onChange={(e) => updItem("seasons", idx, "sales_start", e.target.value)} className={inp} /></div>
                <div><Label className="text-white/70 text-xs">Sales end date</Label><Input type="date" data-testid={`cms-season-end-${idx}`} value={(s.sales_end || "").slice(0, 10)} onChange={(e) => updItem("seasons", idx, "sales_end", e.target.value)} className={inp} /></div>
              </div>
              <div><Label className="text-white/70 text-xs">Heading</Label><Input value={s.heading || ""} onChange={(e) => updItem("seasons", idx, "heading", e.target.value)} className={inp} /></div>
              <div><Label className="text-white/70 text-xs">Description</Label><Textarea value={s.description || ""} onChange={(e) => updItem("seasons", idx, "description", e.target.value)} className={inp} /></div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label className="text-white/70 text-xs">CTA label</Label><Input value={s.cta_label || ""} onChange={(e) => updItem("seasons", idx, "cta_label", e.target.value)} className={inp} /></div>
                <div><Label className="text-white/70 text-xs">CTA URL</Label><Input value={s.cta_url || ""} onChange={(e) => updItem("seasons", idx, "cta_url", e.target.value)} className={inp} /></div>
              </div>
            </div>
          ))}
          {!(cms.seasons || []).length && <p className="text-sm text-white/40">No seasons yet. Add one to feature a seasonal banner on the homepage.</p>}
        </div>
      </GlassCard>

      <GlassCard className="p-6" hover={false}>
        <div className="flex items-center gap-3 mb-4"><h3 className="font-playfair text-lg text-white flex-1">Announcement bar</h3><Switch data-testid="cms-announce-toggle" checked={!!A.enabled} onCheckedChange={(v) => setBlock("announcement_bar", "enabled", v)} /></div>
        <Input data-testid="cms-announce-text" value={A.text || ""} onChange={(e) => setBlock("announcement_bar", "text", e.target.value)} className={inp} />
      </GlassCard>

      <GlassCard className="p-6" hover={false}>
        <h3 className="font-playfair text-lg text-white mb-4">Hero</h3>
        <div className="space-y-4">
          <div><Label className="text-white/80">Eyebrow</Label><Input value={H.eyebrow || ""} onChange={(e) => setBlock("hero", "eyebrow", e.target.value)} className={inp} /></div>
          <div><Label className="text-white/80">Headline</Label><Input data-testid="cms-hero-title" value={H.title || ""} onChange={(e) => setBlock("hero", "title", e.target.value)} className={inp} /></div>
          <div><Label className="text-white/80">Gold supporting line</Label><Input value={H.title2 || ""} onChange={(e) => setBlock("hero", "title2", e.target.value)} className={inp} /></div>
          <div><Label className="text-white/80">Paragraph</Label><Textarea value={H.subtitle || ""} onChange={(e) => setBlock("hero", "subtitle", e.target.value)} className={inp} /></div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div><Label className="text-white/80">CTA primary</Label><Input value={H.cta_primary || ""} onChange={(e) => setBlock("hero", "cta_primary", e.target.value)} className={inp} /></div>
            <div><Label className="text-white/80">CTA secondary</Label><Input value={H.cta_secondary || ""} onChange={(e) => setBlock("hero", "cta_secondary", e.target.value)} className={inp} /></div>
            <div><Label className="text-white/80">CTA tertiary</Label><Input value={H.cta_tertiary || ""} onChange={(e) => setBlock("hero", "cta_tertiary", e.target.value)} className={inp} /></div>
          </div>
        </div>
      </GlassCard>

      <GlassCard className="p-6" hover={false}>
        <div className="flex items-center gap-3 mb-4"><h3 className="font-playfair text-lg text-white flex-1">Countdown</h3><Switch checked={!!C.enabled} onCheckedChange={(v) => setBlock("countdown", "enabled", v)} /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="text-white/80">Title</Label><Input value={C.title || ""} onChange={(e) => setBlock("countdown", "title", e.target.value)} className={inp} /></div>
          <div><Label className="text-white/80">Target date (ISO)</Label><Input type="datetime-local" value={C.target || ""} onChange={(e) => setBlock("countdown", "target", e.target.value)} className={inp} /></div>
        </div>
      </GlassCard>

      <GlassCard className="p-6" hover={false}>
        <div className="flex items-center gap-3 mb-4"><h3 className="font-playfair text-lg text-white flex-1">VIP List</h3><Switch checked={!!V.enabled} onCheckedChange={(v) => setBlock("vip", "enabled", v)} /></div>
        <div className="space-y-4">
          <div><Label className="text-white/80">Title</Label><Input value={V.title || ""} onChange={(e) => setBlock("vip", "title", e.target.value)} className={inp} /></div>
          <div><Label className="text-white/80">Text</Label><Textarea value={V.text || ""} onChange={(e) => setBlock("vip", "text", e.target.value)} className={inp} /></div>
          <div><Label className="text-white/80">Background image URL</Label><Input data-testid="cms-vip-bg" value={V.background || ""} onChange={(e) => setBlock("vip", "background", e.target.value)} className={inp} /></div>
        </div>
      </GlassCard>

      <GlassCard className="p-6" hover={false}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-playfair text-lg text-white">Featured offers</h3>
        </div>
        <p className="text-sm text-white/50">Featured Offers are now managed in the dedicated <a href="/admin/promotions" className="text-gold hover:underline">Promotions</a> area, so campaigns can be scheduled, targeted and auto-expired. Homepage cards are driven by active promotions marked "Show on homepage".</p>
      </GlassCard>

      <GlassCard className="p-6" hover={false}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-playfair text-lg text-white">Shop by occasion <span className="text-sm text-white/40">(alternating image blocks)</span></h3>
          <Button size="sm" variant="outline" className="border-white/20 text-white gap-1" data-testid="cms-add-occasion" onClick={() => addItem("occasions", { enabled: true, heading: "", description: "", image: "", cta_label: "Browse Occasion", cta_url: "/fireworks" })}><Plus className="h-4 w-4" /> Add occasion</Button>
        </div>
        <div className="space-y-5">
          {(cms.occasions || []).map((o, idx) => (
            <div key={idx} className="rounded-xl border border-white/10 bg-black/20 p-4 space-y-3" data-testid={`cms-occasion-${idx}`}>
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-wide text-white/50 flex-1">Occasion {idx + 1}</span>
                <Switch checked={o.enabled !== false} onCheckedChange={(v) => updItem("occasions", idx, "enabled", v)} />
                <button type="button" onClick={() => moveItem("occasions", idx, -1)} className="text-white/50 hover:text-white p-1"><ArrowUp className="h-4 w-4" /></button>
                <button type="button" onClick={() => moveItem("occasions", idx, 1)} className="text-white/50 hover:text-white p-1"><ArrowDown className="h-4 w-4" /></button>
                <button type="button" onClick={() => delItem("occasions", idx)} className="text-firered/80 hover:text-firered p-1" data-testid={`cms-del-occasion-${idx}`}><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label className="text-white/70 text-xs">Heading</Label><Input value={o.heading || ""} onChange={(e) => updItem("occasions", idx, "heading", e.target.value)} className={inp} /></div>
                <div><Label className="text-white/70 text-xs">Image URL</Label><Input value={o.image || ""} onChange={(e) => updItem("occasions", idx, "image", e.target.value)} className={inp} /></div>
              </div>
              <div><Label className="text-white/70 text-xs">Description</Label><Textarea value={o.description || ""} onChange={(e) => updItem("occasions", idx, "description", e.target.value)} className={inp} /></div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label className="text-white/70 text-xs">CTA label</Label><Input value={o.cta_label || ""} onChange={(e) => updItem("occasions", idx, "cta_label", e.target.value)} className={inp} /></div>
                <div><Label className="text-white/70 text-xs">CTA URL</Label><Input value={o.cta_url || ""} onChange={(e) => updItem("occasions", idx, "cta_url", e.target.value)} className={inp} /></div>
              </div>
            </div>
          ))}
          {!(cms.occasions || []).length && <p className="text-sm text-white/40">No occasions yet. Add one to feature it on the homepage.</p>}
        </div>
      </GlassCard>

      <Button data-testid="cms-save-btn" onClick={save} disabled={saving} className="cf-gold-btn font-semibold gap-2">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Homepage</Button>
    </div>
  );
}

function PagesTab() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => { setLoading(true); api.get("/admin/content/pages").then(({ data }) => setPages(data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/content/pages/${editing.key}`, {
        title: editing.title, content_html: editing.content_html, status: editing.status,
        meta_title: editing.meta_title, meta_description: editing.meta_description,
        seo: editing.seo || { auto: true },
      });
      toast.success("Page saved"); setEditing(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin text-gold" /> Loading…</div>;

  if (editing) {
    return (
      <div className="space-y-4" data-testid="content-page-editor">
        <button onClick={() => setEditing(null)} className="text-white/70 hover:text-white text-sm">← Back to pages</button>
        <GlassCard className="p-6 space-y-4" hover={false}>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="text-white/80">Title</Label><Input data-testid="page-title-input" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className={inp} /></div>
            <div><Label className="text-white/80">Status</Label>
              <select className="w-full bg-black/30 border border-white/15 text-white rounded-md px-3 py-2 text-sm mt-1" data-testid="page-status-select" value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                <option value="draft">Draft</option><option value="published">Published</option>
              </select>
            </div>
          </div>
          <div><Label className="text-white/80">Content</Label><div className="mt-1"><RichTextEditor value={editing.content_html} onChange={(v) => setEditing({ ...editing, content_html: v })} /></div></div>
          <div className="border-t border-white/10 pt-4">
            <SeoTab
              entityType="page"
              entity={{ key: editing.key, title: editing.title, name: editing.title, content_html: editing.content_html, slug: editing.slug, status: editing.status, seo: editing.seo }}
              value={editing.seo}
              onChange={(seo) => setEditing({ ...editing, seo })}
              slug={editing.slug}
            />
          </div>
          <Button data-testid="save-page-btn" onClick={save} disabled={saving} className="cf-gold-btn font-semibold gap-2">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Page</Button>
        </GlassCard>
      </div>
    );
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2" data-testid="content-pages-list">
      {pages.map((p) => (
        <GlassCard key={p.key} className="p-5 flex items-center justify-between" data-testid={`content-page-${p.slug}`}>
          <div>
            <p className="text-white font-medium">{p.title}</p>
            <p className="text-xs text-white/40">/{p.slug} · <span className={p.status === "published" ? "text-emerald-400" : "text-amber"}>{p.status}</span></p>
          </div>
          <Button variant="outline" size="sm" className="border-white/20 text-white gap-2" data-testid={`edit-page-${p.slug}`} onClick={() => setEditing(p)}><Pencil className="h-3.5 w-3.5" /> Edit</Button>
        </GlassCard>
      ))}
    </div>
  );
}

export default function AdminContent() {
  return (
    <div className="space-y-6" data-testid="admin-content">
      <div>
        <h1 className="font-playfair text-2xl sm:text-3xl text-white">Homepage & Content</h1>
        <p className="mt-2 text-muted-foreground">Manage homepage copy and editable content pages — no hardcoded content.</p>
      </div>
      <Tabs defaultValue="homepage">
        <TabsList className="bg-white/5 border border-white/10">
          <TabsTrigger value="homepage" data-testid="tab-homepage" className="data-[state=active]:bg-gold/20 data-[state=active]:text-gold gap-2"><Home className="h-4 w-4" /> Homepage</TabsTrigger>
          <TabsTrigger value="pages" data-testid="tab-pages" className="data-[state=active]:bg-gold/20 data-[state=active]:text-gold gap-2"><FileText className="h-4 w-4" /> Content Pages</TabsTrigger>
        </TabsList>
        <TabsContent value="homepage" className="mt-6"><HomepageTab /></TabsContent>
        <TabsContent value="pages" className="mt-6"><PagesTab /></TabsContent>
      </Tabs>
    </div>
  );
}
